import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Bell, BellSlash, GameController, ChatCircleDots, Users, Trophy,
  Storefront, Star, Eye, Handshake, Medal, ShieldCheck, BookOpen, Crown, Lightning, Clock
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { SupportButton, SupportFooter } from '../components/Support';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  session_updates: { label: 'Session Updates', desc: 'Game session invites, joins, cancellations', icon: GameController, color: '#00F0FF' },
  messages: { label: 'Messages', desc: 'Direct and group messages', icon: ChatCircleDots, color: '#9D4CDD' },
  open_seat_alerts: { label: 'Open Seat Alerts', desc: 'Available spots in nearby games', icon: Users, color: '#39FF14' },
  tournament_updates: { label: 'Tournament Updates', desc: 'Registrations, brackets, results', icon: Trophy, color: '#FFD700' },
  shop_events: { label: 'Shop Events', desc: 'New events from shops you follow', icon: Storefront, color: '#F97316' },
  favorite_games: { label: 'Favorite Games', desc: 'Activity for games you love', icon: Star, color: '#EF4444' },
  discovery_recommendations: { label: 'Discovery', desc: 'Personalized event recommendations', icon: Eye, color: '#3B82F6' },
  rivalry_prompts: { label: 'Rivalry Prompts', desc: 'Rematch and rivalry notifications', icon: Handshake, color: '#F59E0B' },
  achievement_updates: { label: 'Achievements', desc: 'Unlocks, level ups, milestones', icon: Medal, color: '#10B981' },
  reminder_alerts: { label: 'Reminders', desc: 'Event and session reminders', icon: Clock, color: '#F97316' },
  moderation_or_system: { label: 'System', desc: 'Platform updates and moderation', icon: ShieldCheck, color: '#888' },
  publisher_content: { label: 'Publisher Content', desc: 'New rules, tutorials from publishers', icon: BookOpen, color: '#3B82F6' },
  premium_or_plan: { label: 'Premium & Plans', desc: 'Subscription and upgrade notifications', icon: Crown, color: '#9D4CDD' },
  admin_internal: { label: 'Admin Internal', desc: 'Internal operational alerts', icon: Lightning, color: '#EF4444' },
};

const FREQ_OPTIONS = [
  { value: 'instant', label: 'Instant' },
  { value: 'batched', label: 'Batched' },
  { value: 'daily_digest', label: 'Daily' },
  { value: 'muted', label: 'Muted' },
];

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/notifications/preferences`, { withCredentials: true })
      .then(({ data }) => setPrefs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updatePref = async (category, field, value) => {
    setPrefs(prev => prev.map(p => p.notification_category === category ? { ...p, [field]: value } : p));
    try {
      await axios.put(`${API_URL}/api/notifications/preferences/${category}`, { [field]: value }, { withCredentials: true });
    } catch {
      toast.error('Failed to save');
      load();
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A12] pb-24" data-testid="notification-settings-page">
      <div className="sticky top-0 z-20 bg-[#0A0A12]/95 backdrop-blur-lg border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white"><ArrowLeft size={20} /></button>
        <h1 className="text-white font-bold text-lg" style={{ fontFamily: 'Unbounded' }}>Notification Settings</h1>
      </div>

      <div className="px-4 py-4 space-y-3">
        {loading ? (
          [...Array(6)].map((_, i) => <div key={i} className="h-20 bg-white/[0.02] rounded-xl animate-pulse" />)
        ) : (
          prefs.map(p => {
            const cat = p.notification_category;
            const meta = CATEGORY_META[cat];
            if (!meta) return null;
            const Icon = meta.icon;
            const enabled = p.in_app_enabled !== false;

            return (
              <div key={cat} className="bg-[#0e0e16] border border-white/[0.04] rounded-xl p-4" data-testid={`pref-${cat}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}12` }}>
                      <Icon size={16} weight="duotone" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <p className="text-white text-[13px] font-semibold">{meta.label}</p>
                      <p className="text-[#555] text-[10px]">{meta.desc}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => updatePref(cat, 'in_app_enabled', !enabled)}
                    className={`w-11 h-6 rounded-full relative transition-colors ${enabled ? 'bg-[#10B981]' : 'bg-[#333]'}`}
                    data-testid={`toggle-${cat}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'left-[22px]' : 'left-0.5'}`} />
                  </button>
                </div>

                {enabled && (
                  <div className="mt-3 flex items-center gap-2 pl-12">
                    <span className="text-[10px] text-[#444]">Frequency:</span>
                    {FREQ_OPTIONS.map(f => (
                      <button key={f.value}
                        onClick={() => updatePref(cat, 'frequency_mode', f.value)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${p.frequency_mode === f.value ? 'bg-white/10 text-white' : 'bg-white/[0.02] text-[#444] hover:text-[#888]'}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Contact Support */}
        <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-3"
          data-testid="settings-contact-support">
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold">Need help?</p>
            <p className="text-[11px] text-[#888] mt-0.5">Email our team with any question or issue.</p>
          </div>
          <SupportButton size="sm" subject="CyBoard Support — Settings" />
        </div>

        <SupportFooter />
      </div>

      <BottomNav />
    </div>
  );
}
